import useProvidersForCampaignType from '@gr/portal/hooks/useProvidersForCampaignType';
import { CampaignTypeEnum } from '@gr/shared/enums';
import { Button, ButtonVariantEnum, Modal } from '@Wonder-Cave/ui';
import { useEffect, useState } from 'react';
import ProviderForClientDropdown from '../shared/Form/Dropdowns/ProviderForClientDropdown';

interface ICampaignSwapProviderModalProps {
  showModal: boolean;
  clientId: string;
  clientName: string;
  campaignType: CampaignTypeEnum;
  campaignTypeLabel: string;
  onClose: () => void;
  onCancel: () => void;
  onSuccess: (providerId: string) => void;
}

const CampaignSwapProviderModal = ({
  showModal,
  clientId,
  clientName,
  campaignType,
  campaignTypeLabel,
  onClose,
  onCancel,
  onSuccess,
}: ICampaignSwapProviderModalProps) => {
  // Only the admin's pick lives in state. The displayed value falls back to the client's currently
  // mapped provider, derived at render rather than seeded into state by an effect.
  const [selectedProviderId, setSelectedProviderId] = useState<string>('');
  const [{ data: providerMapData }, refetchProviderMap] = useProvidersForCampaignType(clientId);
  const currentProviderId = providerMapData?.data?.types?.find((type) => type.name === campaignType)?.providerId;

  useEffect(() => {
    if (!showModal || !clientId) {
      return;
    }

    // Reset the pick each time the modal opens so a previous row's selection can't carry over.
    setSelectedProviderId('');

    const loadProviderMap = async () => {
      try {
        await refetchProviderMap();
      } catch (err) {
        console.error(err);
      }
    };

    loadProviderMap();
  }, [showModal, clientId, campaignType]);

  return (
    <Modal title="Swap Provider" show={showModal} onClose={onClose}>
      <div className="px-8">
        <h3 className="text-center">
          This changes the provider for every {campaignTypeLabel} campaign under {clientName}, not just the campaign
          you selected. Live campaigns are paused when the provider changes.
        </h3>
        <div className="mt-6">
          <ProviderForClientDropdown
            clientId={clientId}
            campaignType={campaignType}
            value={selectedProviderId || currentProviderId}
            onChange={(newValue) => setSelectedProviderId((newValue?.value as string) ?? '')}
          />
        </div>
      </div>
      <div className="flex justify-center mt-8 mb-4">
        <Button variant={ButtonVariantEnum.SECONDARY} onClick={onCancel} className="mr-4 uppercase">
          CANCEL
        </Button>
        <Button
          onClick={() => onSuccess(selectedProviderId)}
          variant={ButtonVariantEnum.PRIMARY}
          className="uppercase"
          disabled={!selectedProviderId || selectedProviderId === currentProviderId}
        >
          SWAP PROVIDER
        </Button>
      </div>
    </Modal>
  );
};

export default CampaignSwapProviderModal;
